// src/components/DashboardBohejuru.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table, Badge, Card, Row, Col, Button, Form,
  InputGroup, Modal, Dropdown, Spinner, Alert,
} from "react-bootstrap";
import axios from "../utils/axiosInstance";
import * as Icon from "react-bootstrap-icons";
import { format, parseISO, differenceInDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Toast, InlineAlert } from "./ui/Toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";

import Message from "../page/MessageKagari";
import RegisterB from "./AddumuyobozBohejuru";
import Messaging from "./Messaging";
import { renderMarkdown, getMarkdownStyles } from "../utils/markdownRenderer";
import Broadcasts from "./Broadcasts";
import Amatangazo from "./Amatangazo";
import IncomingQuestions from "./IncomingQuestions";
import GusomaRaporo from "./GusomaRaporo";
import AmatangazoMudugudu from "./AmatangazoMudugudu";
import RaporoZoherejweAkagari from "./RaporoZoherejweAkagari";
import { VillageNavButton } from "./VillageInfoSystem";
import AbashyitsiAkagari from "./AbashyitsiAkagari";
import IbibazoAkagari from "./IbibazoAkagari";
import AkagariIbirego from "./AkagariIbirego";
import IbiregoAkagari from "./IbiregoAkagari";
import ImicoImyifatire from "./ImicoImyifatire";
import AbanaMuMihanda from "./AbanaMuMihanda";
import GukoraAmaraporo from "./amaraporo";
import ModuleOverviewPage from "./ModuleOverviewPage";
import "../styles/akagari.css";

const API_URL = "/raporo_akagari";
const BASE_URL = "";

// ===================== VALIDATION UTILITIES =====================
const validateNationalId = (value) => {
  if (!value) return { valid: false, error: null, empty: true };
  const cleaned = value.replace(/\s/g, "").replace(/\D/g, "");
  if (cleaned.length === 0) return { valid: false, error: null, empty: true };
  if (!/^\d+$/.test(cleaned)) return { valid: false, error: "Indangamuntu igomba kuba imibare gusa" };
  if (cleaned.length < 3) return { valid: false, error: null };
  if (!cleaned.startsWith("119") && !cleaned.startsWith("120"))
    return { valid: false, error: "Indangamuntu igomba gutangira na 119 cyangwa 120" };
  if (cleaned.length !== 16)
    return { valid: false, error: `Indangamuntu igomba kuba imibare 16 (ubu: ${cleaned.length}/16)` };
  return { valid: true, error: null };
};

const formatNationalId = (value) => {
  const cleaned = (value || "").replace(/\s/g, "").replace(/\D/g, "").slice(0, 16);
  let formatted = "";
  for (let i = 0; i < cleaned.length; i++) {
    if (i === 1 || i === 5 || i === 6 || i === 13 || i === 14) formatted += " ";
    formatted += cleaned[i];
  }
  return formatted;
};

const getNidaPrefix = (value) => {
  if (!value) return null;
  const cleaned = (value || "").replace(/\s/g, "").replace(/\D/g, "");
  if (cleaned.length < 3) return null;
  if (cleaned.startsWith("119")) return { prefix: "119", color: "#10b981", label: "NIDA 119" };
  if (cleaned.startsWith("120")) return { prefix: "120", color: "#06b6d4", label: "NIDA 120" };
  return { prefix: cleaned.substring(0, 3), color: "#ef4444", label: "Ntiyemewe" };
};

const validatePhone = (value) => {
  if (!value) return { valid: false, error: null, empty: true };
  const cleaned = (value || "").replace(/\s/g, "").replace(/\D/g, "");
  if (cleaned.length === 0) return { valid: false, error: null, empty: true };
  if (!/^\d+$/.test(cleaned)) return { valid: false, error: "Nimero igomba kuba imibare gusa" };
  if (cleaned.length < 3) return { valid: false, error: null };
  if (!cleaned.startsWith("07")) return { valid: false, error: "Nimero igomba gutangira na 07" };
  if (cleaned.length >= 3) {
    const p = cleaned.substring(0, 3);
    if (!["078", "079", "072", "073"].includes(p))
      return { valid: false, error: "Nimero igomba gutangira na 078/079 (MTN) cyangwa 072/073 (Airtel)" };
  }
  if (cleaned.length > 3 && cleaned.length !== 10)
    return { valid: false, error: `Nimero igomba kuba 10 (ubu: ${cleaned.length}/10)` };
  if (cleaned.length === 10) return { valid: true, error: null };
  return { valid: false, error: null };
};

const formatPhone = (value) => {
  const c = (value || "").replace(/\s/g, "").replace(/\D/g, "").slice(0, 10);
  if (c.length <= 4) return c;
  if (c.length <= 7) return `${c.slice(0, 4)} ${c.slice(4)}`;
  return `${c.slice(0, 4)} ${c.slice(4, 7)} ${c.slice(7)}`;
};

const getPhoneCarrier = (value) => {
  if (!value) return null;
  const c = (value || "").replace(/\s/g, "").replace(/\D/g, "");
  if (c.length < 3) return null;
  const p = c.substring(0, 3);
  if (p === "078" || p === "079") return { carrier: "MTN", color: "#fbbf24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.3)" };
  if (p === "072" || p === "073") return { carrier: "Airtel", color: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.3)" };
  return null;
};

const cleanNationalId = (v) => (v || "").replace(/\s/g, "").replace(/\D/g, "");
const cleanPhone = (v) => (v || "").replace(/\s/g, "").replace(/\D/g, "");

// ===================== STYLES =====================
const ModernDashboardStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700&display=swap');
    .dashboard-wrapper {
      --font-main:'Nunito Sans',system-ui,sans-serif;
      --bg:#f0f4f8;
      --surface:#ffffff;
      --surface-2:#f8fafc;
      --border:#dde3ea;
      --text:#1a2332;
      --text-soft:#4a5568;
      --text-muted:#718096;
      --primary:#003580;
      --primary-soft:rgba(0,53,128,0.10);
      --primary-light:#1a56b0;
      --accent:#c8a84b;
      --accent-soft:rgba(200,168,75,0.15);
      --success:#1a7a4a;
      --success-soft:rgba(26,122,74,0.12);
      --warning:#b45309;
      --warning-soft:rgba(180,83,9,0.12);
      --danger:#b91c1c;
      --danger-soft:rgba(185,28,28,0.12);
      --cyan:#0369a1;
      --cyan-soft:rgba(3,105,161,0.12);
      --radius:12px;
      --radius-lg:16px;
      --shadow:0 2px 12px rgba(0,53,128,0.08);
      --shadow-hover:0 8px 28px rgba(0,53,128,0.18);
      --transition:cubic-bezier(0.23,1,0.32,1);
      --sidebar-width:260px;
      font-family:var(--font-main);
      background:var(--bg);
      color:var(--text);
      min-height:100vh;
      min-height:100dvh;
      overflow-x:hidden;
      overflow-y:auto;
    }
    .dashboard-wrapper .dashboard-wrapper{display:flex;min-height:100vh;min-height:100dvh;align-items:flex-start}
    .dashboard-wrapper .main-content{flex:1;padding:2rem 2.5rem;transition:margin-left .35s cubic-bezier(0.4,0,0.2,1);min-width:0;background:var(--bg);min-height:100vh;color:var(--text)}
    @media(min-width:992px){.dashboard-wrapper .main-content{margin-left:280px}}
    @media(max-width:991.98px){.dashboard-wrapper .main-content{margin-left:0!important;padding:1.25rem 1rem;padding-bottom:calc(72px + env(safe-area-inset-bottom,0px))}}
    @media(max-width:575px){.dashboard-wrapper .main-content{padding:1rem 0.75rem;padding-bottom:calc(72px + env(safe-area-inset-bottom,0px))}}

    /* ── Sidebar ── */
    .dashboard-wrapper .sidebar{
      width:280px;height:100vh;height:100dvh;
      position:fixed;left:0;top:0;
      background:linear-gradient(180deg,#002a6b 0%,#003580 50%,#003d99 100%);
      display:flex;flex-direction:column;
      z-index:1050;
      box-shadow:4px 0 24px rgba(0,53,128,0.22);
      transition:transform .35s cubic-bezier(0.4,0,0.2,1);
      overflow:hidden;
      will-change:transform;
    }
    @media(min-width:992px){
      .dashboard-wrapper .sidebar{transform:translateX(0)!important;}
    }
    @media(max-width:991.98px){
      .dashboard-wrapper .sidebar{width:min(84vw,300px);transform:translateX(-100%);}
      .dashboard-wrapper .sidebar.sidebar-open{transform:translateX(0)!important;}
    }
    /* ── Sidebar nav section must scroll, logout must stay pinned ── */
    .dashboard-wrapper .nav-section{
      flex:1 1 0;
      min-height:0;
      overflow-y:auto;
      overflow-x:hidden;
      -webkit-overflow-scrolling:touch;
      scrollbar-width:thin;
      scrollbar-color:rgba(255,255,255,0.2) transparent;
    }
    .dashboard-wrapper .nav-section::-webkit-scrollbar{width:3px;}
    .dashboard-wrapper .nav-section::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.18);border-radius:4px;}
    /* ── Logout section — always pinned at bottom ── */
    .dashboard-wrapper .sidebar-logout-section{
      flex-shrink:0;
      padding:0.75rem;
      border-top:1px solid rgba(255,255,255,0.12);
      padding-bottom:calc(0.75rem + env(safe-area-inset-bottom,0px));
      background:rgba(0,0,0,0.12);
    }
    .dashboard-wrapper .sidebar-logout-btn{
      display:flex;align-items:center;gap:0.75rem;
      width:100%;padding:0.85rem 1.25rem;
      border-radius:10px;border:none;
      background:transparent;
      color:rgba(255,255,255,0.82);
      font-size:0.92rem;font-weight:500;
      cursor:pointer;
      transition:all .25s ease;
      text-align:left;
      min-height:48px;
      -webkit-tap-highlight-color:transparent;
      touch-action:manipulation;
    }
    .dashboard-wrapper .sidebar-logout-btn:hover,
    .dashboard-wrapper .sidebar-logout-btn:focus-visible{
      background:rgba(185,28,28,0.18);
      color:#fca5a5;
      outline:none;
    }
    .dashboard-wrapper .sidebar-logout-btn:active{
      transform:scale(0.97);
    }
    @media(max-width:575px){
      .dashboard-wrapper .sidebar-logout-btn{
        min-height:52px;
        font-size:0.95rem;
      }
    }

    /* ── Backdrop ── */
    .akagari-sidebar-backdrop{
      display:none;position:fixed;inset:0;
      background:rgba(0,0,0,0.55);
      z-index:1040;opacity:0;
      transition:opacity .3s ease;
      pointer-events:none;
    }
    @media(max-width:991.98px){
      .akagari-sidebar-backdrop{display:block;}
      .akagari-sidebar-backdrop.visible{opacity:1;pointer-events:auto;}
    }

    /* ── Sidebar close btn ── */
    .akagari-sidebar-close{
      display:none;position:absolute;top:12px;right:12px;
      width:36px;height:36px;border-radius:50%;
      background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.2);
      color:#fff;align-items:center;justify-content:center;
      cursor:pointer;z-index:10;transition:background .2s ease;
    }
    .akagari-sidebar-close:hover{background:rgba(255,255,255,0.22);}
    @media(max-width:991.98px){.akagari-sidebar-close{display:flex;}}

    /* ── Body scroll lock ── */
    body.sidebar-lock{overflow:hidden;touch-action:none;}
    .dashboard-wrapper .brand-section{padding:1.75rem 1.5rem;border-bottom:1px solid rgba(255,255,255,0.15);background:rgba(0,0,0,0.15)}
    .dashboard-wrapper .nav-section{flex:1 1 0;min-height:0;padding:1.25rem 0.75rem;overflow-y:auto}
    .dashboard-wrapper .nav-link-vertical{display:flex;align-items:center;padding:.85rem 1.25rem;color:rgba(255,255,255,0.8);border-radius:10px;margin:.3rem .5rem;font-weight:500;cursor:pointer;transition:all .3s var(--transition);user-select:none;font-size:0.92rem}
    .dashboard-wrapper .nav-link-vertical:hover{background:rgba(255,255,255,0.12);color:#fff}
    .dashboard-wrapper .nav-link-vertical.active{background:linear-gradient(135deg,#c8a84b,#d4b96a);color:#1a2332;font-weight:700;box-shadow:0 4px 12px rgba(200,168,75,0.35)}
    .dashboard-wrapper .header-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:1.25rem 1.75rem;margin-bottom:2rem;display:flex;align-items:center;justify-content:space-between;box-shadow:var(--shadow);position:sticky;top:0;z-index:200;backdrop-filter:blur(12px);border-left:4px solid var(--primary)}
    .dashboard-wrapper .stat-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);transition:all .4s var(--transition);box-shadow:var(--shadow);overflow:hidden;color:var(--text)}
    .dashboard-wrapper .stat-card:hover{transform:translateY(-4px);box-shadow:var(--shadow-hover)}
    .dashboard-wrapper .stat-card h3{color:var(--primary)!important}
    .dashboard-wrapper .content-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:2rem;box-shadow:var(--shadow);color:var(--text)}
    .dashboard-wrapper .chart-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:1.5rem;box-shadow:var(--shadow);height:100%;color:var(--text);min-width:0;overflow:hidden}
    .dashboard-wrapper .form-page-wrapper{max-width:720px;margin:0 auto}
    .dashboard-wrapper .form-label,.dashboard-wrapper label{color:var(--text)!important;font-weight:600;margin-bottom:.5rem}
    .dashboard-wrapper .form-control,.dashboard-wrapper .form-select{background:var(--surface-2);border:1px solid var(--border);color:var(--text);border-radius:10px;padding:.75rem 1rem;transition:all .3s var(--transition)}
    .dashboard-wrapper .form-control:focus,.dashboard-wrapper .form-select:focus{border-color:var(--primary);box-shadow:0 0 0 3px var(--primary-soft);background:#fff;color:var(--text)}
    .dashboard-wrapper .form-control::placeholder{color:var(--text-muted)}
    .dashboard-wrapper .table-modern-wrapper{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);box-shadow:var(--shadow);overflow:hidden}
    .dashboard-wrapper .table-modern-wrapper .table-responsive-inner{overflow-x:auto;-webkit-overflow-scrolling:touch}
    .dashboard-wrapper .table-modern{background-color:var(--surface);color:var(--text);table-layout:auto}
    .dashboard-wrapper .table-modern thead th{background:linear-gradient(135deg,#003580,#1a56b0);color:#fff;padding:1rem 1.25rem;font-size:.8rem;text-transform:uppercase;letter-spacing:.8px;border-bottom:none;white-space:nowrap}
    .dashboard-wrapper .table-modern th.col-amazina{min-width:200px}
    .dashboard-wrapper .table-modern th.col-nid{min-width:210px}
    .dashboard-wrapper .table-modern th.col-email{min-width:200px}
    .dashboard-wrapper .table-modern th.col-phone{min-width:160px}
    .dashboard-wrapper .table-modern th.col-date{min-width:120px}
    .dashboard-wrapper .table-modern th.col-action{min-width:80px}
    .dashboard-wrapper .table-modern td{background-color:var(--surface);padding:1rem 1.25rem;vertical-align:middle;border-bottom:1px solid var(--border);color:var(--text)}
    .dashboard-wrapper .village-name-text{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:140px}
    .dashboard-wrapper .table-modern tr:hover td{background:#f0f4f8;color:var(--primary)}
    .dashboard-wrapper .btn-primary,.dashboard-wrapper .raporo-attachment-btn{background:var(--primary);border:none;border-radius:10px;padding:.8rem 1.6rem;font-weight:600;transition:all .3s var(--transition);color:#fff}
    .dashboard-wrapper .btn-primary:hover,.dashboard-wrapper .raporo-attachment-btn:hover{background:var(--primary-light);transform:translateY(-1px);box-shadow:0 8px 20px rgba(0,53,128,0.3)}
    .dashboard-wrapper .btn-icon{width:44px;height:44px;padding:0;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;transition:all .3s var(--transition)}
    .dashboard-wrapper .btn-icon:hover{background:var(--primary-soft);transform:scale(1.1)}
    .dashboard-wrapper .btn-icon-sm{width:38px;height:38px;padding:0;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;transition:all .3s var(--transition)}
    .dashboard-wrapper .btn-icon-sm:hover{transform:scale(1.08)}
    .dashboard-wrapper .search-modern{border-radius:10px;background:var(--surface-2);border:1px solid var(--border);overflow:hidden}
    .dashboard-wrapper .form-control-modern{background:transparent;border:none;color:var(--text)}
    .dashboard-wrapper .form-control-modern::placeholder{color:var(--text-muted)}
    .raporo-detail-modal .modal-content,.logout-modal .modal-content,.delete-modal .modal-content,.village-update-modal .modal-content{background:var(--surface);border:1px solid var(--border);border-radius:20px;color:var(--text);box-shadow:0 20px 60px rgba(0,53,128,0.15)}
    .dashboard-wrapper .raporo-header{background:linear-gradient(135deg,var(--primary),var(--primary-light));border-bottom:none;border-radius:20px 20px 0 0;padding:2rem;text-align:center}
    .dashboard-wrapper .raporo-title{font-size:1.8rem;font-weight:800;margin-bottom:1.5rem;color:#fff}
    .dashboard-wrapper .delete-toast{position:fixed;top:1.5rem;right:1.5rem;z-index:2000;min-width:320px}
    .dashboard-wrapper .alert-success{background:rgba(26,122,74,.12);border:1px solid rgba(26,122,74,.4);color:#14532d}
    .dashboard-wrapper .alert-danger{background:rgba(185,28,28,.12);border:1px solid rgba(185,28,28,.4);color:#7f1d1d}
    .dashboard-wrapper .logout-section{cursor:pointer;transition:all .3s var(--transition);border-radius:10px;margin:.5rem;padding:.75rem}
    .dashboard-wrapper .logout-section:hover{background:rgba(255,255,255,0.1)}
    .dashboard-wrapper .village-avatar{width:42px;height:42px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:.9rem;color:#fff;flex-shrink:0}
    .dashboard-wrapper .village-carrier-badge,.dashboard-wrapper .village-nida-badge{display:inline-flex;align-items:center;gap:.3rem;padding:.2rem .55rem;border-radius:20px;font-size:.7rem;font-weight:600}
    .dashboard-wrapper .village-empty{text-align:center;padding:5rem 2rem}
    .dashboard-wrapper .village-empty-icon{width:120px;height:120px;border-radius:50%;background:var(--primary-soft);display:flex;align-items:center;justify-content:center;margin:0 auto 2rem}
    .dashboard-wrapper .update-field{margin-bottom:1.5rem}
    .dashboard-wrapper .update-field label{font-size:.82rem;text-transform:uppercase;letter-spacing:.5px;margin-bottom:.6rem;display:flex;align-items:center;gap:.5rem;color:var(--text-soft)!important}
    .dashboard-wrapper .update-field .field-wrapper{position:relative}
    .dashboard-wrapper .update-field .field-indicator{position:absolute;right:12px;top:50%;transform:translateY(-50%);display:flex;align-items:center;gap:6px}
    .dashboard-wrapper .update-modal-header{background:linear-gradient(135deg,var(--primary) 0%,var(--primary-light) 100%);border-radius:20px 20px 0 0;padding:2rem 2.5rem;border:none}
    .dashboard-wrapper .text-soft{color:var(--text-soft)!important}
    .dashboard-wrapper .text-muted-custom{color:var(--text-muted)!important}

    /* ADDED: Registration success message styles */
    .register-success-toast {
      position: fixed;
      top: 1.5rem;
      right: 1.5rem;
      z-index: 2100;
      min-width: 360px;
      max-width: 460px;
    }

    @media (max-width: 575px) {
      .register-success-toast {
        min-width: auto;
        left: 1rem;
        right: 1rem;
      }
    }

    .register-success-card {
      background: var(--surface);
      border: 1px solid rgba(0,53,128,0.2);
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 12px 40px rgba(0,53,128,0.15);
    }

    .register-success-header {
      background: linear-gradient(135deg, rgba(0,53,128,0.08) 0%, rgba(200,168,75,0.08) 100%);
      padding: 1.1rem 1.4rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      border-bottom: 1px solid var(--border);
      position: relative;
    }

    .register-success-header::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--primary), var(--accent));
    }

    .register-success-check {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: var(--success-soft);
      border: 2px solid rgba(26,122,74,0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .register-success-body {
      padding: 1.1rem 1.4rem;
    }

    .register-success-detail {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.35rem 0;
      font-size: 0.82rem;
      color: var(--text-soft);
    }

    .register-success-detail-icon {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .register-success-footer {
      padding: 0.75rem 1.4rem 1.1rem;
      display: flex;
      gap: 0.75rem;
    }

    .register-success-btn {
      flex: 1;
      padding: 0.55rem 1rem;
      border-radius: 8px;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
    }

    .register-success-btn-primary {
      background: var(--primary-soft);
      border: 1px solid rgba(0,53,128,0.25);
      color: var(--primary);
    }

    .register-success-btn-primary:hover {
      background: rgba(0,53,128,0.15);
    }

    .register-success-btn-ghost {
      background: transparent;
      border: 1px solid var(--border);
      color: var(--text-soft);
    }

    .register-success-btn-ghost:hover {
      background: var(--surface-2);
      color: var(--text);
    }

    .register-success-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: none;
      border: none;
      color: var(--text-soft);
      cursor: pointer;
      padding: 0;
      font-size: 1.1rem;
      opacity: 0.6;
      transition: opacity 0.2s;
    }

    .register-success-close:hover {
      opacity: 1;
    }

    @media(max-width:768px){
      .dashboard-wrapper .table-modern thead{display:none}
      .dashboard-wrapper .table-modern tr{display:block;margin:1rem 0;border:1px solid var(--border);border-radius:var(--radius-lg);padding:1.25rem;background:var(--surface)}
      .dashboard-wrapper .table-modern td{display:flex;justify-content:space-between;border:none;padding:.7rem 0;color:var(--text)}
      .dashboard-wrapper .table-modern td::before{content:attr(data-label);font-weight:600;color:var(--text-soft);flex:1}
    }
    @media(max-width:575px){
      .dashboard-wrapper .content-card{padding:1.25rem}
      .dashboard-wrapper .chart-card{padding:1rem}
      .dashboard-wrapper .header-card{padding:1rem 1.25rem;margin-bottom:1.25rem}
    }
    /* ── Sidebar nav responsive ── */
    .dashboard-wrapper .nav-link-vertical{
      min-height:44px;
      -webkit-tap-highlight-color:transparent;
      touch-action:manipulation;
    }
    @media(max-width:991px){
      .dashboard-wrapper .brand-section{padding:1.5rem 1.25rem}
      .dashboard-wrapper .nav-section{padding:1rem 0.5rem}
      .dashboard-wrapper .nav-link-vertical{font-size:0.95rem;padding:.9rem 1rem}
    }
    @media(max-width:575px){
      .dashboard-wrapper .brand-section{padding:1.25rem 1rem}
      .dashboard-wrapper .nav-link-vertical{font-size:0.9rem;padding:.8rem .875rem}
    }
    @media(max-width:360px){
      .dashboard-wrapper .nav-link-vertical{font-size:0.85rem;padding:.75rem .75rem}
    }

    /* ── Charts grid responsive ── */
    .charts-row-responsive > [class*="col-"] {
      min-width: 0;
    }
    @media(max-width:767px){
      .charts-row-responsive > [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
      }
      .dashboard-wrapper .chart-card { height: auto !important; }
    }

    /* ── Stat cards ── */
    @media(max-width:399px){
      .dashboard-wrapper .stat-card .fs-3 { font-size: 1.4rem !important; }
      .dashboard-wrapper .stat-card h3 { font-size: 1.5rem !important; }
    }

    /* ── Header card title ── */
    @media(max-width:400px){
      .dashboard-wrapper .header-card h4 { font-size: 0.88rem !important; }
    }

    /* ── Search + filter row ── */
    .raporo-toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1.5rem;
    }
    .raporo-toolbar .search-modern {
      flex: 1 1 260px;
      min-width: 0;
      max-width: 420px;
    }
    @media(max-width:575px){
      .raporo-toolbar { flex-direction: column; align-items: stretch; }
      .raporo-toolbar .search-modern { max-width: 100%; }
      .raporo-toolbar .dropdown { width: 100%; }
      .raporo-toolbar .dropdown > button { width: 100%; }
    }

    /* ── Modal stats row ── */
    @media(max-width:575px){
      .raporo-detail-modal .modal-body .row > [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
      }
    }

    /* ── Village tab search row ── */
    .village-toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1.5rem;
    }
    .village-toolbar .search-modern {
      flex: 1 1 260px;
      min-width: 0;
      max-width: 420px;
    }
    @media(max-width:575px){
      .village-toolbar { flex-direction: column; align-items: stretch; }
      .village-toolbar .search-modern { max-width: 100%; }
    }

    /* ── Update modal form ── */
    @media(max-width:575px){
      .village-update-modal .modal-body .row > [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
      }
    }

    /* ── Raporo table footer ── */
    @media(max-width:480px){
      .dashboard-wrapper .raporo-table-footer {
        flex-direction: column;
        gap: 0.75rem;
        align-items: center;
        text-align: center;
      }
    }

    /* ── Page strip / header strip ── */
    @media(max-width:480px){
      .dashboard-wrapper .header-card {
        flex-wrap: wrap;
        gap: 0.5rem;
      }
    }

    /* ── Stat card icon ── */
    @media(max-width:480px){
      .dashboard-wrapper .stat-card .rounded-3 {
        width: 44px !important;
        height: 44px !important;
      }
    }
  `}</style>
);

// ===================== VILLAGE TAB =====================
const VillageTab = ({ currentUserId }) => {
  const [villages, setVillages] = useState([]);
  const [filteredVillages, setFilteredVillages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [feedback, setFeedback] = useState(null);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [updateForm, setUpdateForm] = useState({ fullname: "", email: "", telefone: "", national_id: "" });
  const [updateErrors, setUpdateErrors] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);

  const itemsPerPage = 8;

  const fetchVillages = useCallback(async () => {
    if (!currentUserId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/get_mudugudu/${currentUserId}`);
      setVillages(res.data);
    } catch (err) {
      console.error("Failed to fetch villages:", err);
      setFeedback({ type: "danger", message: "Ntibyashobotse gukuramo imidugudu." });
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => { fetchVillages(); }, [fetchVillages]);

  useEffect(() => {
    let result = [...villages];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter((v) =>
        [v.fullname, v.email, v.telefone, v.national_id].some((f) => (f || "").toLowerCase().includes(t))
      );
    }
    result.sort((a, b) => {
      let vA = sortKey === "created_at" ? new Date(a[sortKey] || 0) : (a[sortKey] || "");
      let vB = sortKey === "created_at" ? new Date(b[sortKey] || 0) : (b[sortKey] || "");
      if (typeof vA === "string") vA = vA.toLowerCase();
      if (typeof vB === "string") vB = vB.toLowerCase();
      if (vA < vB) return sortDir === "asc" ? -1 : 1;
      if (vA > vB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredVillages(result);
    setCurrentPage(0);
  }, [searchTerm, villages, sortKey, sortDir]);

  const offset = currentPage * itemsPerPage;
  const currentItems = filteredVillages.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredVillages.length / itemsPerPage);

  const handleSort = (key) => {
    setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const getAvatarColor = (name) => {
    const colors = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899", "#14b8a6"];
    return colors[(name || "A").charCodeAt(0) % colors.length];
  };
  const getInitials = (name) => {
    if (!name) return "?";
    const p = name.trim().split(/\s+/);
    return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const openUpdateModal = (village) => {
    setSelectedVillage(village);
    setUpdateForm({
      fullname: village.fullname || "",
      email: village.email || "",
      telefone: formatPhone(village.telefone || ""),
      national_id: formatNationalId(village.national_id || ""),
    });
    setUpdateErrors({});
    setShowUpdateModal(true);
  };

  const handleFieldChange = (field, value) => {
    let formatted = value;
    if (field === "telefone") formatted = formatPhone(value);
    else if (field === "national_id") formatted = formatNationalId(value);
    setUpdateForm((prev) => ({ ...prev, [field]: formatted }));
    if (updateErrors[field]) setUpdateErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validateUpdateForm = () => {
    const e = {};
    if (!updateForm.fullname.trim()) e.fullname = "Amazina arakenewe";
    else if (updateForm.fullname.trim().length < 3) e.fullname = "Nibura inyuguti 3";
    if (!updateForm.email.trim()) e.email = "Imeyili irakenewe";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updateForm.email)) e.email = "Imeyili ntiyanditse neza";
    const pv = validatePhone(cleanPhone(updateForm.telefone));
    if (!pv.valid) e.telefone = pv.error || "Telefone ntiyuzuye";
    const nv = validateNationalId(cleanNationalId(updateForm.national_id));
    if (!nv.valid) e.national_id = nv.error || "Indangamuntu ntiyuzuye";
    setUpdateErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = async (ev) => {
    ev.preventDefault();
    if (!validateUpdateForm()) return;
    try {
      setUpdateLoading(true);
      const payload = {
        fullname: updateForm.fullname.trim(),
        email: updateForm.email.trim(),
        telefone: cleanPhone(updateForm.telefone),
        national_id: cleanNationalId(updateForm.national_id),
      };
      await axios.put(`${BASE_URL}/update_mudugudu/${selectedVillage.user_id}`, payload);
      setFeedback({ type: "success", message: `"${payload.fullname}" yavuguruwe neza!` });
      setShowUpdateModal(false);
      fetchVillages();
    } catch (err) {
      const msg = err.response?.data?.error || "Ntibyashobotse kuvugurura.";
      setFeedback({ type: "danger", message: msg });
    } finally {
      setUpdateLoading(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  return (
    <motion.div key="villages" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -40 }} className="delete-toast">
            <Alert variant={feedback.type === "success" ? "success" : "danger"} dismissible onClose={() => setFeedback(null)} className="m-0">
              <strong>{feedback.type === "success" ? "Byagenze neza! " : "Ikosa! "}</strong>{feedback.message}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="d-flex align-items-center gap-3 mb-4">
        <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon.GeoAltFill size={24} color="var(--primary)" />
        </div>
        <div style={{ minWidth: 0 }}>
          <h5 className="fw-bold mb-0 text-truncate">Imidugudu Yanditswe</h5>
          <small style={{ color: "var(--text-soft)" }}>{villages.length} {villages.length === 1 ? "umudugudu" : "imidugudu"}</small>
        </div>
      </div>

      <div className="village-toolbar">
        <InputGroup className="search-modern">
          <InputGroup.Text style={{ background: "transparent", border: "none", color: "var(--text-soft)" }}><Icon.Search size={18} /></InputGroup.Text>
          <Form.Control placeholder="Shakisha amazina, email, telefone..." className="form-control-modern" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          {searchTerm && (
            <InputGroup.Text style={{ background: "transparent", border: "none", cursor: "pointer" }} onClick={() => setSearchTerm("")}>
              <Icon.XCircleFill size={16} color="var(--text-soft)" />
            </InputGroup.Text>
          )}
        </InputGroup>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <Badge bg="dark" className="px-3 py-2" style={{ border: "1px solid var(--border)", borderRadius: 10, fontSize: "0.85rem" }}>
            {filteredVillages.length} / {villages.length}
          </Badge>
          <Button variant="outline-light" size="sm" onClick={fetchVillages} style={{ borderRadius: 12, padding: "0.6rem 1rem", whiteSpace: "nowrap" }}>
            <Icon.ArrowClockwise className="me-1" size={16} />Vugurura
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 my-5">
          <Spinner animation="border" variant="primary" style={{ width: "3.5rem", height: "3.5rem" }} />
          <p className="mt-3" style={{ color: "var(--text-soft)" }}>Imidugudu irimo gutegurwa...</p>
        </div>
      ) : villages.length === 0 && !searchTerm ? (
        <motion.div className="village-empty" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="village-empty-icon"><Icon.GeoAlt size={56} color="var(--primary)" /></div>
          <h4 className="fw-bold mb-2">Nta mudugudu wanditswe</h4>
          <p style={{ color: "var(--text-soft)", maxWidth: 420, margin: "0 auto" }}>Nta mudugudu ubonetse kuri iki Kagari.</p>
        </motion.div>
      ) : (
        <div className="table-modern-wrapper">
          <div className="table-responsive-inner">
          <Table hover className="table-modern mb-0">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th className="col-amazina" onClick={() => handleSort("fullname")} role="button" style={{ cursor: "pointer" }}>
                  <div className="d-flex align-items-center gap-2">Amazina {sortKey === "fullname" && <Icon.ChevronExpand size={12} />}</div>
                </th>
                <th className="col-nid" onClick={() => handleSort("national_id")} role="button" style={{ cursor: "pointer" }}>
                  <div className="d-flex align-items-center gap-2">Indangamuntu {sortKey === "national_id" && <Icon.ChevronExpand size={12} />}</div>
                </th>
                <th className="col-email" onClick={() => handleSort("email")} role="button" style={{ cursor: "pointer" }}>
                  <div className="d-flex align-items-center gap-2">Imeyili {sortKey === "email" && <Icon.ChevronExpand size={12} />}</div>
                </th>
                <th className="col-phone" onClick={() => handleSort("telefone")} role="button" style={{ cursor: "pointer" }}>
                  <div className="d-flex align-items-center gap-2">Telefone {sortKey === "telefone" && <Icon.ChevronExpand size={12} />}</div>
                </th>
                <th className="col-date" onClick={() => handleSort("created_at")} role="button" style={{ cursor: "pointer" }}>
                  <div className="d-flex align-items-center gap-2">Italiki {sortKey === "created_at" && <Icon.ChevronExpand size={12} />}</div>
                </th>
                <th className="col-action text-center">Ibikorwa</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5" style={{ color: "var(--text-soft)" }}>
                  <Icon.Search size={28} className="mb-2 d-block mx-auto" style={{ opacity: 0.5 }} />Nta mudugudu ubonetse
                </td></tr>
              ) : (
                currentItems.map((v, idx) => {
                  const carrier = getPhoneCarrier(v.telefone);
                  const nida = getNidaPrefix(v.national_id);
                  return (
                    <motion.tr key={v.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}>
                      <td data-label="#"><span style={{ fontSize: ".85rem", color: "var(--text-soft)" }}>{offset + idx + 1}</span></td>
                      <td data-label="Amazina">
                        <div className="d-flex align-items-center gap-3">
                          <div className="village-avatar" style={{ background: getAvatarColor(v.fullname) }}>{getInitials(v.fullname)}</div>
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold village-name-text">{v.fullname}</div>
                            <div style={{ fontSize: ".75rem", color: "var(--text-muted)" }}>ID: {v.user_id}</div>
                          </div>
                        </div>
                      </td>
                      <td data-label="Indangamuntu">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <code style={{ color: "var(--cyan)", fontSize: ".85rem", background: "rgba(6,182,212,0.08)", padding: ".25rem .5rem", borderRadius: 8 }}>
                            {formatNationalId(v.national_id || "")}
                          </code>
                          {nida && <span className="village-nida-badge" style={{ background: `${nida.color}18`, color: nida.color }}>{nida.label}</span>}
                        </div>
                      </td>
                      <td data-label="Imeyili">
                        <div className="d-flex align-items-center gap-2">
                          <Icon.EnvelopeFill size={13} color="var(--text-muted)" />
                          <span style={{ fontSize: ".88rem" }}>{v.email}</span>
                        </div>
                      </td>
                      <td data-label="Telefone">
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: ".88rem" }}>{formatPhone(v.telefone || "")}</span>
                          {carrier && <span className="village-carrier-badge" style={{ background: carrier.bg, color: carrier.color, border: `1px solid ${carrier.border}` }}>{carrier.carrier}</span>}
                        </div>
                      </td>
                      <td data-label="Italiki">
                        {v.created_at ? (
                          <div>
                            <div style={{ fontSize: ".85rem" }}>{(() => { try { return format(parseISO(v.created_at), "dd MMM yyyy"); } catch { return "—"; } })()}</div>
                            <div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>{(() => { try { return format(parseISO(v.created_at), "HH:mm"); } catch { return ""; } })()}</div>
                          </div>
                        ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                      </td>
                      <td data-label="Ibikorwa" className="text-center">
                        <Button variant="outline-primary" className="btn-icon-sm" onClick={() => openUpdateModal(v)} title="Vugurura">
                          <Icon.PencilSquare size={16} />
                        </Button>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </Table>
          </div>
          {pageCount > 1 && (
            <div className="p-3 border-top d-flex justify-content-between align-items-center" style={{ borderColor: "var(--border)" }}>
              <small style={{ color: "var(--text-soft)" }}>Paji {currentPage + 1} kuri {pageCount}</small>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-light" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)} style={{ borderRadius: 10 }}>
                  <Icon.ChevronLeft size={14} /> Ibanze
                </Button>
                <Button size="sm" variant="outline-light" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => p + 1)} style={{ borderRadius: 10 }}>
                  Ikurikira <Icon.ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={showUpdateModal} onHide={() => !updateLoading && setShowUpdateModal(false)} centered size="lg" className="village-update-modal">
        <Modal.Header className="update-modal-header border-0" closeButton closeVariant="white">
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon.PencilSquare size={22} color="white" />
            </div>
            <div>
              <h5 className="fw-bold mb-0 text-white">Vugurura Amakuru</h5>
              <small style={{ color: "rgba(255,255,255,0.7)" }}>{selectedVillage?.fullname} • ID: {selectedVillage?.user_id}</small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleUpdate}>
            <Row className="g-4">
              <Col md={6}>
                <div className="update-field">
                  <label><Icon.PersonFill size={14} color="var(--primary)" /> Amazina Yuzuye</label>
                  <div className="field-wrapper">
                    <Form.Control type="text" value={updateForm.fullname} onChange={(e) => handleFieldChange("fullname", e.target.value)} placeholder="Amazina yuzuye" isInvalid={!!updateErrors.fullname} disabled={updateLoading} style={{ paddingRight: 40 }} />
                    {updateForm.fullname && !updateErrors.fullname && <span className="field-indicator"><Icon.CheckCircleFill size={16} color="var(--success)" /></span>}
                    <Form.Control.Feedback type="invalid">{updateErrors.fullname}</Form.Control.Feedback>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="update-field">
                  <label><Icon.EnvelopeFill size={14} color="var(--success)" /> Imeyili</label>
                  <div className="field-wrapper">
                    <Form.Control type="email" value={updateForm.email} onChange={(e) => handleFieldChange("email", e.target.value)} placeholder="example@email.com" isInvalid={!!updateErrors.email} disabled={updateLoading} style={{ paddingRight: 40 }} />
                    {updateForm.email && !updateErrors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updateForm.email) && <span className="field-indicator"><Icon.CheckCircleFill size={16} color="var(--success)" /></span>}
                    <Form.Control.Feedback type="invalid">{updateErrors.email}</Form.Control.Feedback>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="update-field">
                  <label><Icon.CreditCard2FrontFill size={14} color="var(--cyan)" /> Indangamuntu</label>
                  <div className="field-wrapper">
                    <Form.Control type="text" value={updateForm.national_id} onChange={(e) => handleFieldChange("national_id", e.target.value)} placeholder="1 1990 8 0000000 0 00" isInvalid={!!updateErrors.national_id} disabled={updateLoading} style={{ paddingRight: 90 }} />
                    <span className="field-indicator">
                      {getNidaPrefix(updateForm.national_id) && <span className="village-nida-badge" style={{ background: `${getNidaPrefix(updateForm.national_id).color}18`, color: getNidaPrefix(updateForm.national_id).color }}>{getNidaPrefix(updateForm.national_id).label}</span>}
                      {validateNationalId(cleanNationalId(updateForm.national_id)).valid && <Icon.CheckCircleFill size={16} color="var(--success)" />}
                    </span>
                    <Form.Control.Feedback type="invalid">{updateErrors.national_id}</Form.Control.Feedback>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="update-field">
                  <label><Icon.TelephoneFill size={14} color="var(--warning)" /> Telefone</label>
                  <div className="field-wrapper">
                    <Form.Control type="text" value={updateForm.telefone} onChange={(e) => handleFieldChange("telefone", e.target.value)} placeholder="07XX XXX XXX" isInvalid={!!updateErrors.telefone} disabled={updateLoading} style={{ paddingRight: 100 }} />
                    <span className="field-indicator">
                      {getPhoneCarrier(updateForm.telefone) && <span className="village-carrier-badge" style={{ background: getPhoneCarrier(updateForm.telefone).bg, color: getPhoneCarrier(updateForm.telefone).color, border: `1px solid ${getPhoneCarrier(updateForm.telefone).border}` }}>{getPhoneCarrier(updateForm.telefone).carrier}</span>}
                      {validatePhone(cleanPhone(updateForm.telefone)).valid && <Icon.CheckCircleFill size={16} color="var(--success)" />}
                    </span>
                    <Form.Control.Feedback type="invalid">{updateErrors.telefone}</Form.Control.Feedback>
                  </div>
                </div>
              </Col>
            </Row>
            <div className="d-flex gap-3 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <Button variant="outline-light" onClick={() => setShowUpdateModal(false)} disabled={updateLoading} style={{ borderRadius: 14, padding: ".75rem 2rem" }}>Hagarika</Button>
              <Button type="submit" variant="primary" disabled={updateLoading} style={{ borderRadius: 14, padding: ".75rem 2.5rem" }}>
                {updateLoading ? <><Spinner animation="border" size="sm" className="me-2" />Birimo kuvugurura...</> : <><Icon.CheckLg className="me-2" size={18} />Emeza Ivugurura</>}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

// ===================== AKAGARI IBIBAZO PANEL =====================
const AkagariIbibazoPaneli = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [replies, setReplies]   = useState({});
  const [sending, setSending]   = useState({});
  const [toast, setToast]       = useState(null);
  const [filter, setFilter]     = useState("all");

  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchProblems = useCallback(async () => {
    try {
      const res = await axios.get("/ibibazo/akagari");
      setProblems(Array.isArray(res.data) ? res.data : []);
    } catch { setProblems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchProblems();
    const iv = setInterval(fetchProblems, 8000);
    return () => clearInterval(iv);
  }, [fetchProblems]);

  const handleReply = async (id) => {
    const msg = (replies[id] || "").trim();
    if (!msg) return;
    setSending(p => ({ ...p, [id]: true }));
    try {
      await axios.post(`/ibibazo/reply/${id}`, { reply_message: msg });
      setReplies(p => ({ ...p, [id]: "" }));
      notify("✅ Igisubizo cyoherejwe neza!");
      fetchProblems();
    } catch { notify("❌ Habaye ikosa. Ongera ugerageze.", "danger"); }
    finally { setSending(p => ({ ...p, [id]: false })); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleString("rw-RW"); } catch { return d; } };

  const filtered = filter === "pending"
    ? problems.filter(p => p.status === "pending")
    : filter === "answered"
    ? problems.filter(p => p.status === "answered")
    : problems;

  const pendingCount = problems.filter(p => p.status === "pending").length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h5 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{ color: "var(--primary)" }}>
            <Icon.EnvelopeOpenFill size={20} />
            Ibibazo by'Imidugudu
          </h5>
          <p className="text-muted mb-0 small">Subiza ibibazo by'abayobozi b'imidugudu.</p>
        </div>
        {pendingCount > 0 && (
          <Badge bg="warning" text="dark" className="fs-6 px-3 py-2">
            <Icon.ClockFill className="me-1" size={14} />{pendingCount} itegereje igisubizo
          </Badge>
        )}
      </div>

      {toast && (
        <Alert variant={toast.type === "success" ? "success" : "danger"} dismissible onClose={() => setToast(null)} className="mb-3">
          {toast.msg}
        </Alert>
      )}

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[{ key: "all", label: "Byose", icon: "bi-list-ul" }, { key: "pending", label: "Bitegereje", icon: "bi-clock" }, { key: "answered", label: "Byasubijwe", icon: "bi-check-circle" }].map(f => (
          <Button key={f.key} size="sm" variant={filter === f.key ? "primary" : "outline-secondary"}
            style={{ borderRadius: 20 }} onClick={() => setFilter(f.key)}>
            <i className={`bi ${f.icon} me-1`} />{f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2 text-muted">Birimo gutegurwa...</p></div>
      ) : filtered.length === 0 ? (
        <div className="content-card text-center py-5">
          <Icon.Inbox size={48} className="text-muted mb-3" />
          <p className="text-muted fw-semibold">Nta bibazo bibonetse.</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {[...filtered].sort((a, b) => (a.status === "pending" ? -1 : 1)).map(p => (
            <div key={p.id} className="card border-0 shadow-sm"
              style={{ borderLeft: `4px solid ${p.status === "pending" ? "#ffc107" : "#198754"}` }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                      style={{ width: 40, height: 40, background: "var(--primary)", fontSize: "0.85rem" }}>
                      {(p.sender_name || "U").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="fw-bold">{p.sender_name || "Umudugudu"}</div>
                      <small className="text-muted">{fmtDate(p.created_at)}</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold" style={{ fontSize: 13, color: "var(--primary)" }}>{p.title}</span>
                    <Badge bg={p.status === "answered" ? "success" : "warning"} text={p.status === "answered" ? undefined : "dark"}>
                      {p.status === "answered" ? "✅ Yasubijwe" : "⏳ Itegereje"}
                    </Badge>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="fw-semibold text-muted small mb-1"><Icon.QuestionCircle className="me-1" size={13} />IKIBAZO:</div>
                  <div className="p-3 rounded-3" style={{ background: "#f8f9fa", fontSize: 14 }}>{p.message}</div>
                </div>

                {p.status === "answered" && p.reply_message && (
                  <div className="mb-3">
                    <div className="fw-semibold text-success small mb-1"><Icon.ChatDotsFill className="me-1" size={13} />IGISUBIZO CYATANZWE:</div>
                    <div className="p-3 rounded-3 border border-success" style={{ background: "#f0fff4", fontSize: 14 }}>{p.reply_message}</div>
                  </div>
                )}

                {p.status === "pending" && (
                  <div>
                    <div className="fw-semibold text-primary small mb-2"><Icon.ChatDotsFill className="me-1" size={13} />SUBIZA:</div>
                    <Form.Control as="textarea" rows={3} placeholder="Andika igisubizo cyawe hano..."
                      value={replies[p.id] || ""}
                      onChange={e => setReplies(prev => ({ ...prev, [p.id]: e.target.value }))}
                      className="mb-2" style={{ resize: "vertical" }}
                    />
                    <Button size="sm" variant="primary" style={{ borderRadius: 10 }}
                      disabled={sending[p.id] || !(replies[p.id] || "").trim()}
                      onClick={() => handleReply(p.id)}>
                      {sending[p.id]
                        ? <><Spinner animation="border" size="sm" className="me-2" />Birimo koherezwa...</>
                        : <><Icon.SendFill className="me-2" size={13} />Ohereza Igisubizo</>}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== NAV GROUPS =====================
// Same grouped structure as the Isibo dashboard: each entry is one sidebar
// button. A group with a single item navigates straight to that item's tab;
// a group with several items opens a ModuleOverviewPage card grid (see the
// `activeTab === group.id` branch further below) listing its children.
const AKAGARI_NAV_GROUPS = [
  { id: "village-info", icon: Icon.HouseGear, label: "Amakuru y'Imidugudu", items: [{ key: "village-info", label: "Amakuru y'Imidugudu" }] },
  { id: "raporo", icon: Icon.Grid, label: "Statistics", items: [{ key: "raporo", label: "Statistics" }] },
  { id: "subiza-ibibazo", icon: Icon.ChatSquareDotsFill, label: "Subiza Ibibazo", items: [{ key: "subiza-ibibazo", label: "Subiza Ibibazo" }] },
  { id: "ibibazo-isibo", icon: Icon.ChatSquareQuoteFill, label: "Ibibazo by'Isibo", items: [{ key: "ibibazo-isibo", label: "Ibibazo by'Isibo" }] },
  {
    id: "grp-raporo-zoherejwe", icon: Icon.SendFill, iconStr: "send-fill", label: "Raporo Zoherejwe", color: "#0047AB",
    description: "Raporo zoherejwe, gusoma raporo, abashyitsi n'abantu bo mu muhanda.",
    items: [
      { key: "raporo-forwarded", icon: "send-fill", label: "Raporo Zoherejwe", color: "#0047AB", description: "Raporo zoherejwe na Umudugudu" },
      { key: "gusoma-raporo", icon: "file-earmark-text", label: "Gusoma Raporo", color: "#0047AB", description: "Soma raporo wakiriye" },
      { key: "abashyitsi-kagari", icon: "people-fill", label: "Reba Abashyitsi", color: "#0047AB", description: "Kurikirana abashyitsi bagusuye" },
      { key: "abana-mu-mihanda", icon: "signpost-split", label: "Abantu bo mu Muhanda", color: "#0047AB", description: "Abantu bo mu muhanda" },
    ],
  },
  {
    id: "grp-ubutumwa", icon: Icon.ChatSquareText, iconStr: "chat-square-text", label: "Ubutumwa", color: "#7c3aed",
    description: "Ubutumwa, aho baganirira n'amatangazo.",
    items: [
      { key: "messages", icon: "chat-square-text", label: "Ubutumwa", color: "#7c3aed", description: "Ubutumwa bwose" },
      { key: "ahobaganirira", icon: "chat-dots", label: "Aho baganirira", color: "#7c3aed", description: "Ganira n'abandi bayobozi" },
      { key: "amatangazo", icon: "megaphone", label: "Amatangazo", color: "#7c3aed", description: "Amatangazo yashyizweho" },
    ],
  },
  {
    id: "grp-umutekano", icon: Icon.FileEarmarkText, iconStr: "file-earmark-text", label: "Umutekano", color: "#dc2626",
    description: "Ibirego biturutse mu Mutekano no mu Isibo.",
    items: [
      { key: "ibirego", icon: "file-earmark-text", label: "Ibirego", color: "#dc2626", description: "Ibirego byoherejwe n'Umutekano" },
      { key: "isibo-ibirego", icon: "file-earmark-text", label: "IBIREGO", color: "#dc2626", description: "Ibirego biturutse ku Isibo" },
    ],
  },
  { id: "grp-ibyangobwa", icon: Icon.AwardFill, label: "Ibyangobwa", items: [{ key: "imico-imyifatire", label: "Imico n'imyifatire" }] },
  {
    id: "grp-abayobozi", icon: Icon.PersonPlus, iconStr: "person-plus", label: "Abayobozi", color: "#0f766e",
    description: "Ongeraho, burura cyangwa uhindure abayobozi.",
    items: [
      { key: "ongeraho", icon: "person-plus", label: "Ongeraho Umuyobozi", color: "#0f766e", description: "Andika umuyobozi mushya" },
      { id: "barura-umuyobozi", key: "villages", icon: "list-ul", label: "Barura Umuyobozi", color: "#0f766e", description: "Urutonde rw'abayobozi" },
      { id: "hindura-umuyobozi", key: "villages", icon: "geo-alt-fill", label: "Hindura Umuyobozi", color: "#0f766e", description: "Hindura amakuru y'umuyobozi" },
    ],
  },
];

// True if `tab` is this group's own id or belongs to one of its children —
// used to keep the parent sidebar button highlighted while viewing a child.
const akagariGroupIsActive = (group, tab) => tab === group.id || group.items.some((it) => it.key === tab);

// ===================== SIDEBAR CONTENT =====================
const SidebarContent = ({ activeTab, setActiveTab, closeSidebar, setShowLogout, expandedSubmenu, setExpandedSubmenu }) => (
    <>
      {/* Close button — visible on mobile/tablet only */}
      <button className="akagari-sidebar-close" onClick={closeSidebar} aria-label="Funga">
        <Icon.X size={18} />
      </button>

      <div className="brand-section">
        <h4 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: "#fff", fontSize: "clamp(1rem, 2.5vw, 1.25rem)" }}>
          <Icon.BuildingsFill size={22} color="#c8a84b" /><span>Akagari</span>
        </h4>
        <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", marginTop: "4px", letterSpacing: "0.06em", textTransform: "uppercase" }}></div>
      </div>

      <div className="nav-section">
        {AKAGARI_NAV_GROUPS.map((group) => {
          const destination = group.items.length === 1 ? group.items[0].key : group.id;
          const isActive = akagariGroupIsActive(group, activeTab);
          return (
            <div
              key={group.id}
              className={`nav-link-vertical ${isActive ? "active" : ""}`}
              onClick={() => { setActiveTab(destination); closeSidebar(); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (setActiveTab(destination), closeSidebar())}
            >
              <group.icon size={18} className="nav-icon" />
              <span>{group.label}</span>
            </div>
          );
        })}

        {/* Gukora Amaraporo — expandable submenu */}
        <div className="nav-link-vertical" role="button" tabIndex={0}
          onClick={() => setExpandedSubmenu(p => p === "amaraporo" ? null : "amaraporo")}
          style={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderRadius:10, marginBottom:2, background: activeTab === "gukora-amaraporo" || activeTab === "word" || activeTab === "excel" ? 'rgba(255,255,255,0.12)' : 'transparent', color: activeTab === "gukora-amaraporo" || activeTab === "word" || activeTab === "excel" ? '#fff' : 'rgba(255,255,255,0.75)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Icon.Files size={18} className="nav-icon" />
            <span style={{ fontSize:'0.85rem', fontWeight:500 }}>Gukora Amaraporo</span>
          </div>
          <Icon.ChevronDown size={14} style={{ transform: expandedSubmenu === "amaraporo" ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.2s' }} />
        </div>

        {/* Submenu items */}
        <div style={{ overflow:'hidden', maxHeight: expandedSubmenu === "amaraporo" ? 100 : 0, transition:'max-height 0.3s ease' }}>
          <div className={`nav-link-vertical subnav-item ${activeTab === "word" ? "active" : ""}`} role="button" tabIndex={0}
            onClick={() => { setActiveTab("word"); closeSidebar(); setExpandedSubmenu("amaraporo"); }}
            style={{ paddingLeft:44, fontSize:'0.8rem' }}>
            <Icon.FileWord size={16} style={{ marginRight:8 }} /> Koresha Word Document
          </div>
          <div className={`nav-link-vertical subnav-item ${activeTab === "excel" ? "active" : ""}`} role="button" tabIndex={0}
            onClick={() => { setActiveTab("excel"); closeSidebar(); setExpandedSubmenu("amaraporo"); }}
            style={{ paddingLeft:44, fontSize:'0.8rem' }}>
            <Icon.FileExcel size={16} style={{ marginRight:8 }} /> Koresha Excel
          </div>
        </div>
      </div>

      <div className="sidebar-logout-section">
        <button
          className="sidebar-logout-btn"
          onClick={() => setShowLogout(true)}
          aria-label="Gusohoka — Sohoka muri konti"
        >
          <Icon.BoxArrowRight size={20} style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}>Gusohoka</div>
            <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)", marginTop: 2 }}>Sohoka muri konti</div>
          </div>
        </button>
      </div>
    </>
);

// ===================== MAIN DASHBOARD =====================
const DashboardBohejuru = () => {
  const [activeTab, setActiveTab] = useState("raporo");
  const [raporo, setRaporo] = useState([]);
  const [filteredRaporo, setFilteredRaporo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("raporo_id");
  const [sortDir, setSortDir] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  // Sidebar open state — used on mobile/tablet
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Lock body scroll when sidebar open on mobile/tablet
  useEffect(() => {
    if (showMobileSidebar) {
      document.body.classList.add('sidebar-lock');
    } else {
      document.body.classList.remove('sidebar-lock');
    }
    return () => document.body.classList.remove('sidebar-lock');
  }, [showMobileSidebar]);

  const toggleSidebar = useCallback(() => setShowMobileSidebar(prev => !prev), []);
  const closeSidebar = useCallback(() => setShowMobileSidebar(false), []);
  const [showLogout, setShowLogout] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateFilter, setDateFilter] = useState("all");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleteFeedback, setDeleteFeedback] = useState(null);

  // ADDED: Registration success message state
  const [registerSuccess, setRegisterSuccess] = useState(null);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  const navigate = useNavigate();
  const itemsPerPage = 10;

  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.user_id || payload.id || null;
      }
    } catch (e) { console.error("Token parse error:", e); }
    return null;
  }, []);

  const fetchData = useCallback(async () => {
    if (activeTab !== "raporo") return;
    try {
      setLoading(true);
      const res = await axios.get(API_URL);
      setRaporo(res.data);
      setFilteredRaporo(res.data);
    } catch (err) { console.error("Failed to fetch reports:", err); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { if (activeTab === "raporo") fetchData(); }, [activeTab, fetchData]);

  useEffect(() => {
    if (activeTab !== "raporo") return;
    let filtered = [...raporo];
    if (searchTerm) filtered = filtered.filter((r) => Object.values(r).some((v) => String(v || "").toLowerCase().includes(searchTerm.toLowerCase())));
    if (dateFilter !== "all") {
      const today = new Date();
      filtered = filtered.filter((r) => { const d = differenceInDays(today, parseISO(r.itariki_ya_raporo)); return dateFilter === "today" ? d === 0 : dateFilter === "week" ? d <= 7 : true; });
    }
    filtered.sort((a, b) => { let vA = sortKey === "itariki_ya_raporo" ? new Date(a[sortKey]) : a[sortKey]; let vB = sortKey === "itariki_ya_raporo" ? new Date(b[sortKey]) : b[sortKey]; if (vA < vB) return sortDir === "asc" ? -1 : 1; if (vA > vB) return sortDir === "asc" ? 1 : -1; return 0; });
    setFilteredRaporo(filtered);
    setCurrentPage(0);
  }, [searchTerm, dateFilter, sortKey, sortDir, raporo, activeTab]);

  const offset = currentPage * itemsPerPage;
  const currentItems = filteredRaporo.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredRaporo.length / itemsPerPage);

  const stats = useMemo(() => ({
    residents: raporo.reduce((s, r) => s + (r.umubare_w_abaturage || 0), 0),
    houses: raporo.reduce((s, r) => s + (r.umubare_w_ingo || 0), 0),
    teens: raporo.reduce((s, r) => s + (r.abanyantege_nke || 0), 0),
    reports: raporo.length,
  }), [raporo]);

  const chartData = useMemo(() => {
    return [...raporo].sort((a, b) => new Date(a.itariki_ya_raporo) - new Date(b.itariki_ya_raporo)).map((r) => ({
      date: format(parseISO(r.itariki_ya_raporo), "MMM dd"),
      abaturage: r.umubare_w_abaturage || 0, abageze: r.umubare_w_ingo || 0, urubyiruko: r.abanyantege_nke || 0,
    }));
  }, [raporo]);

  const handleSort = (key) => { setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc"); setSortKey(key); };
  const requestDelete = (r) => { setReportToDelete(r); setShowDeleteConfirm(true); };
  const performDelete = () => {
    if (!reportToDelete) return;
    setRaporo((p) => p.filter((r) => r.raporo_id !== reportToDelete.raporo_id));
    setDeleteFeedback({ type: "success", message: "Raporo yasibwe neza!" });
    setShowDeleteConfirm(false); setReportToDelete(null);
    setTimeout(() => setDeleteFeedback(null), 4000);
  };

  const validationProps = useMemo(() => ({ validateNationalId, formatNationalId, getNidaPrefix, cleanNationalId, validatePhone, formatPhone, getPhoneCarrier, cleanPhone }), []);

  // ADDED: Handle registration success from RegisterB
  const handleRegisterSuccess = useCallback((registeredData) => {
    setRegisterSuccess({
      name: registeredData?.fullname || "Umuyobozi mushya",
      timestamp: new Date().toLocaleTimeString("rw-RW", { hour: "2-digit", minute: "2-digit" }),
    });

    // Auto-dismiss after 8 seconds
    setTimeout(() => setRegisterSuccess(null), 8000);
  }, []);

  // ADDED: Dismiss registration message
  const dismissRegisterSuccess = useCallback(() => {
    setRegisterSuccess(null);
  }, []);

  // ADDED: Dismiss and navigate to raporo tab
  const goToRaporoAfterRegister = useCallback(() => {
    setRegisterSuccess(null);
    setActiveTab("raporo");
    fetchData();
  }, [fetchData]);

  return (
    <div className="dashboard-wrapper">
      <ModernDashboardStyles />

      <AnimatePresence>
        {deleteFeedback && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -40 }} className="delete-toast">
            <div className={`alert alert-${deleteFeedback.type} alert-dismissible fade show m-0`}><strong>{deleteFeedback.type === "success" ? "Byagenze neza! " : "Ikibazo! "}</strong>{deleteFeedback.message}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ADDED: Registration success toast notification */}
      <AnimatePresence>
        {registerSuccess && (
          <motion.div
            className="register-success-toast"
            initial={{ opacity: 0, y: -30, x: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, x: 30, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
          >
            <div className="register-success-card">
              {/* Header */}
              <div className="register-success-header">
                <motion.div
                  className="register-success-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 250, damping: 12 }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
                  >
                    <Icon.CheckLg size={20} color="var(--success)" />
                  </motion.div>
                </motion.div>
                <div>
                  <div className="fw-bold" style={{ color: "var(--success)", fontSize: "0.95rem" }}>
                    Kwiyandikisha Byagenze Neza!
                  </div>
                  <div style={{ color: "var(--text-soft)", fontSize: "0.78rem" }}>
                    Umuyobozi mushya yongerwaho
                  </div>
                </div>
                <button className="register-success-close" onClick={dismissRegisterSuccess}>
                  <Icon.X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="register-success-body">
                <div className="register-success-detail">
                  <div className="register-success-detail-icon" style={{ background: "var(--primary-soft)" }}>
                    <Icon.PersonFill size={14} color="var(--primary)" />
                  </div>
                  <span style={{ color: "var(--text)" }}>
                    <strong>{registerSuccess.name}</strong> yanditswe neza
                  </span>
                </div>
                <div className="register-success-detail">
                  <div className="register-success-detail-icon" style={{ background: "var(--success-soft)" }}>
                    <Icon.ClockFill size={14} color="var(--success)" />
                  </div>
                  <span>Isaha: {registerSuccess.timestamp}</span>
                </div>
                <div className="register-success-detail">
                  <div className="register-success-detail-icon" style={{ background: "var(--cyan-soft)" }}>
                    <Icon.ShieldCheck size={14} color="var(--cyan)" />
                  </div>
                  <span>Ubu arashobora kwinjira mu buryo bwe</span>
                </div>
              </div>

              {/* Footer */}
              <div className="register-success-footer">
                <button className="register-success-btn register-success-btn-primary" onClick={dismissRegisterSuccess}>
                  <Icon.PersonPlus size={14} />
                  Andikisha undi
                </button>
                <button className="register-success-btn register-success-btn-ghost" onClick={goToRaporoAfterRegister}>
                  <Icon.Grid size={14} />
                  Raporo
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop overlay — mobile/tablet only */}
      <div
        className={`akagari-sidebar-backdrop ${showMobileSidebar ? 'visible' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Sidebar — unified for all screen sizes */}
      <div className={`sidebar ${showMobileSidebar ? 'sidebar-open' : ''}`}>
        <SidebarContent
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          closeSidebar={closeSidebar}
          setShowLogout={setShowLogout}
          expandedSubmenu={expandedSubmenu}
          setExpandedSubmenu={setExpandedSubmenu}
        />
      </div>

      <div className="main-content akagari-main-with-bottom-nav">
        <div className="header-card akagari-header-card">
          <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
            <Button
              variant="link"
              className="p-0 d-lg-none"
              style={{ color: "var(--primary)", minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, position: "relative" }}
              onClick={toggleSidebar}
            >
              <Icon.List size={26} />
            </Button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h4 className="m-0 fw-bold text-truncate" style={{ color: "var(--primary)", fontSize: "clamp(0.95rem, 2.5vw, 1.25rem)" }}>
                {activeTab === "raporo" ? "Raporo" :
                 activeTab === "gusoma-raporo" ? "Gusoma Raporo" :
                 activeTab === "raporo-forwarded" ? "Raporo Zoherejwe na Umudugudu" :
                 activeTab === "villages" ? "Imidugudu" :
                 activeTab === "messages" ? "Ubutumwa" :
                 activeTab === "ahobaganirira" ? "Ahobaganirira" :
                 activeTab === "amatangazo" ? "Amatangazo" :
                 activeTab === "ibibazo" ? "Ibibazo by'Imidugudu" :
                 activeTab === "subiza-ibibazo" ? "Subiza Ibibazo" :
                 activeTab === "abashyitsi-kagari" ? "Reba Abashyitsi Dufite" :
                 activeTab === "ibibazo-isibo" ? "Reba Ibibazo Bivuye ku Isibo" :
                 activeTab === "ibirego" ? "Ibirego byoherejwe n'Umutekano" :
                 activeTab === "gukora-amaraporo" ? "Gukora Amaraporo" :
                 activeTab === "word" ? "Koresha Word Document" :
                 activeTab === "excel" ? "Koresha Excel" :
                 activeTab === "grp-raporo-zoherejwe" ? "Raporo Zoherejwe" :
                 activeTab === "grp-ubutumwa" ? "Ubutumwa" :
                 activeTab === "grp-umutekano" ? "Umutekano" :
                 activeTab === "grp-abayobozi" ? "Abayobozi" :
                 "Ongeraho Umuyobozi"}
              </h4>
              <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>AKAGARI — </div>
            </div>
          </div>
          {/* Logout — always visible in header */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowLogout(true)}
            style={{ borderRadius: 10, display: "flex", alignItems: "center", gap: 6, flexShrink: 0, minHeight: 36, padding: "0.4rem 0.85rem", borderColor: "var(--border)", color: "var(--text-soft)", fontSize: "0.78rem", fontWeight: 600 }}
            title="Gusohoka"
          >
            <Icon.BoxArrowRight size={16} />
            <span>Sohoka</span>
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "raporo" ? (
            <motion.div key="raporo" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
              <Row className="g-3 mb-4">
                {[
                  { title: "Abaturage", val: stats.residents, icon: Icon.PeopleFill, color: "#003580", bg: "rgba(0,53,128,0.08)" },
                  { title: "Abageze mu z'abakuru", val: stats.houses, icon: Icon.HouseDoorFill, color: "#1a7a4a", bg: "rgba(26,122,74,0.08)" },
                  { title: "Urubyiruko", val: stats.teens, icon: Icon.EmojiSmileFill, color: "#b45309", bg: "rgba(180,83,9,0.08)" },
                  { title: "Raporo Zose", val: stats.reports, icon: Icon.Files, color: "#0369a1", bg: "rgba(3,105,161,0.08)" },
                ].map((s, i) => (
                  <Col xs={6} md={3} key={i}><Card className="stat-card h-100"><Card.Body className="p-3 p-md-4 text-center"><div className="mb-2 mb-md-3 mx-auto d-flex align-items-center justify-content-center rounded-3" style={{ width: 48, height: 48, background: s.bg }}><s.icon size={24} color={s.color} /></div><h3 className="fw-bold mb-1" style={{ color: s.color, fontSize: "clamp(1.2rem, 3vw, 1.75rem)" }}>{s.val.toLocaleString()}</h3><div style={{ color: "var(--text-soft)", fontSize: "clamp(0.75rem, 2vw, 0.9rem)", lineHeight: 1.3 }}>{s.title}</div></Card.Body></Card></Col>
                ))}
              </Row>

              <Row className="g-4 mb-5 charts-row-responsive">
                <Col xs={12} md={6} lg={4}>
                  <div className="chart-card"><h5 className="fw-bold mb-4" style={{ color: "var(--primary)" }}>Ubwiyongere bw'Abaturage</h5><div style={{ width: "100%", height: 220, minWidth: 0 }}><ResponsiveContainer width="100%" height={220}><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)" /><XAxis dataKey="date" stroke="var(--text-soft)" fontSize={11} /><YAxis stroke="var(--text-soft)" fontSize={11} width={36} /><RechartsTooltip contentStyle={{ backgroundColor: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "12px" }} itemStyle={{ color: "var(--text)" }} /><Line type="monotone" dataKey="abaturage" stroke="var(--primary)" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} /></LineChart></ResponsiveContainer></div></div>
                </Col>
                <Col xs={12} md={6} lg={4}>
                  <div className="chart-card"><h5 className="fw-bold mb-4" style={{ color: "var(--success)" }}>Abageze mu z'abakuru</h5><div style={{ width: "100%", height: 220, minWidth: 0 }}><ResponsiveContainer width="100%" height={220}><BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)" /><XAxis dataKey="date" stroke="var(--text-soft)" fontSize={11} /><YAxis stroke="var(--text-soft)" fontSize={11} width={36} /><RechartsTooltip contentStyle={{ backgroundColor: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "12px" }} itemStyle={{ color: "var(--text)" }} /><Bar dataKey="abageze" fill="var(--success)" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div></div>
                </Col>
                <Col xs={12} md={12} lg={4}>
                  <div className="chart-card"><h5 className="fw-bold mb-4" style={{ color: "var(--warning)" }}>Ubwiyongere bw'Urubyiruko</h5><div style={{ width: "100%", height: 220, minWidth: 0 }}><ResponsiveContainer width="100%" height={220}><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="var(--border)" /><XAxis dataKey="date" stroke="var(--text-soft)" fontSize={11} /><YAxis stroke="var(--text-soft)" fontSize={11} width={36} /><RechartsTooltip contentStyle={{ backgroundColor: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "12px" }} itemStyle={{ color: "var(--text)" }} /><Line type="monotone" dataKey="urubyiruko" stroke="var(--warning)" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} /></LineChart></ResponsiveContainer></div></div>
                </Col>
              </Row>

              <div className="raporo-toolbar">
                <InputGroup className="search-modern">
                  <InputGroup.Text style={{ background: "transparent", border: "none" }}><Icon.Search size={20} /></InputGroup.Text>
                  <Form.Control placeholder="Shakisha raporo..." className="form-control-modern" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </InputGroup>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-light" className="px-4 py-2" style={{ borderRadius: "14px" }}><Icon.Filter className="me-2" />{dateFilter === "all" ? "Igihe cyose" : dateFilter === "today" ? "Uyu munsi" : "Icyumweru gishize"}</Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setDateFilter("all")}>Igihe cyose</Dropdown.Item>
                    <Dropdown.Item onClick={() => setDateFilter("today")}>Uyu munsi</Dropdown.Item>
                    <Dropdown.Item onClick={() => setDateFilter("week")}>Icyumweru gishize</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {loading ? (
                <div className="text-center py-5 my-5"><div className="spinner-border text-primary" style={{ width: "3.5rem", height: "3.5rem" }} /><p className="mt-3 text-soft">Ibyo birimo gutegurwa...</p></div>
              ) : (
                <div className="raporo-table-wrapper">
                  <div style={{ overflowX: "auto" }}>
                  <Table hover className="raporo-table mb-0">
                    <thead><tr>
                      <th onClick={() => handleSort("umutwe_wa_raporo")} role="button">Umutwe wa Raporo</th>
                      <th onClick={() => handleSort("itariki_ya_raporo")} role="button">Itariki</th>
                      <th>Umudugudu</th>
                      <th className="text-center">Abaturage</th>
                      <th className="text-center">Abageze mu z&#39;abukuru</th>
                      <th className="text-center">Ibikorwa</th>
                    </tr></thead>
                    <tbody>
                      {currentItems.length === 0 ? (
                        <tr><td colSpan="6">
                          <div className="raporo-empty">
                            <div className="raporo-empty-icon"><Icon.FileEarmarkX size={32} color="#a0aec0" /></div>
                            <div style={{ fontWeight: 600, color: "#4a5568" }}>Nta raporo yabonetse</div>
                          </div>
                        </td></tr>
                      ) : currentItems.map((r) => (
                        <tr key={r.raporo_id}>
                          <td data-label="Umutwe wa Raporo">
                            <div className="raporo-title-cell">
                              <div className="raporo-name">{r.umutwe_wa_raporo}</div>
                              <div className="raporo-desc">{r.ibisobanuro}</div>
                            </div>
                          </td>
                          <td data-label="Itariki"><span className="raporo-date">{format(parseISO(r.itariki_ya_raporo), "dd MMM yyyy")}</span></td>
                          <td data-label="Umudugudu"><span className="raporo-village-badge"><Icon.GeoAltFill size={11} />{r.yakozwe_na}</span></td>
                          <td data-label="Abaturage" className="text-center"><span className={`raporo-num ${!r.umubare_w_abaturage ? "raporo-num-zero" : ""}`}>{r.umubare_w_abaturage || 0}</span></td>
                          <td data-label="Abageze mu z'abukuru" className="text-center"><span className={`raporo-num ${!r.umubare_w_ingo ? "raporo-num-zero" : ""}`}>{r.umubare_w_ingo || 0}</span></td>
                          <td data-label="Ibikorwa">
                            <div className="raporo-actions">
                              <button className="raporo-action-btn view" title="Reba" onClick={() => navigate(`/view-report/${r.raporo_id}`)}><Icon.EyeFill size={15} /></button>
                              <a className={`raporo-action-btn download ${!r.attachment ? "disabled" : ""}`} title="Kuramo" href={r.attachment ? `${BASE_URL}${r.attachment}` : undefined} download={!!r.attachment} style={{ pointerEvents: r.attachment ? "auto" : "none", opacity: r.attachment ? 1 : 0.3, textDecoration: "none" }}><Icon.Download size={15} /></a>
                              <button className="raporo-action-btn delete" title="Siba" onClick={() => requestDelete(r)}><Icon.TrashFill size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </div>
                  <div className="raporo-table-footer">
                    <span className="page-info">Paji {currentPage + 1} kuri {pageCount || 1}</span>
                    <div className="page-btns">
                      <button className="page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}><Icon.ChevronLeft size={13} /> Ibanze</button>
                      <button className="page-btn" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => p + 1)}>Ikurikira <Icon.ChevronRight size={13} /></button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

          ) : AKAGARI_NAV_GROUPS.find((g) => g.id === activeTab && g.items.length > 1) ? (
            <motion.div key={activeTab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
              <div className="content-card">
                {(() => {
                  const group = AKAGARI_NAV_GROUPS.find((g) => g.id === activeTab);
                  return (
                    <ModuleOverviewPage
                      icon={group.iconStr}
                      color={group.color}
                      label={group.label}
                      description={group.description}
                      items={group.items}
                      onNavigate={setActiveTab}
                    />
                  );
                })()}
              </div>
            </motion.div>

          ) : activeTab === "village-info" ? (
            <motion.div key="village-info" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
              <div className="content-card" style={{ padding: 0, overflow: "hidden", background: "transparent", border: "none", boxShadow: "none" }}>
                <div style={{ display: "flex", justifyContent: "center", padding: "2rem 1rem" }}>
                  <VillageNavButton role="akagari" />
                </div>
              </div>
            </motion.div>

          ) : activeTab === "gusoma-raporo" ? (
            <motion.div key="gusoma-raporo" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
              <div className="content-card">
                <GusomaRaporo />
              </div>
            </motion.div>

          ) : activeTab === "raporo-forwarded" ? (
            <motion.div key="raporo-forwarded" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
              <div className="content-card">
                <RaporoZoherejweAkagari />
              </div>
            </motion.div>

          ) : activeTab === "villages" ? (
            <VillageTab currentUserId={currentUserId} />

          ) : activeTab === "messages" ? (
            <motion.div key="messages" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}><div className="content-card"><Message /></div></motion.div>

          ) : activeTab === "ahobaganirira" ? (
            <motion.div key="ahobaganirira" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}><div className="content-card"><Messaging /></div></motion.div>

          ) : activeTab === "amatangazo" ? (
            <motion.div key="amatangazo" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
              <div className="content-card">
                <Amatangazo />
              </div>
            </motion.div>

          ) : activeTab === "ibibazo" ? (
            <motion.div key="ibibazo" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
              <div className="content-card">
                <IncomingQuestions />
              </div>
            </motion.div>

          ) : activeTab === "subiza-ibibazo" ? (
            <motion.div key="subiza-ibibazo" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
              <div className="content-card">
                <AkagariIbibazoPaneli />
              </div>
            </motion.div>

          ) : activeTab === "abashyitsi-kagari" ? (
            <motion.div key="abashyitsi-kagari" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><AbashyitsiAkagari /></div>
            </motion.div>
          ) : activeTab === "ibibazo-isibo" ? (
            <motion.div key="ibibazo-isibo" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><IbibazoAkagari /></div>
            </motion.div>
          ) : activeTab === "ibirego" ? (
            <motion.div key="ibirego" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><AkagariIbirego /></div>
            </motion.div>
          ) : activeTab === "isibo-ibirego" ? (
            <motion.div key="isibo-ibirego" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><IbiregoAkagari /></div>
            </motion.div>
          ) : activeTab === "imico-imyifatire" ? (
            <motion.div key="imico-imyifatire" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><ImicoImyifatire mode="cell" /></div>
            </motion.div>
          ) : activeTab === "abana-mu-mihanda" ? (
            <motion.div key="abana-mu-mihanda" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }}>
              <div className="content-card"><AbanaMuMihanda mode="cell" /></div>
            </motion.div>
          ) : activeTab === "ongeraho" ? (
            <motion.div key="ongeraho" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
              <div className="content-card form-page-wrapper">
                <div className="text-center mb-5"><Icon.PersonPlus size={48} color="var(--primary)" className="mb-3" /><h3 className="fw-bold mb-2">Ongeraho Umuyobozi Mushya</h3><p className="text-soft">Uzuzamo amakuru nyayo.</p></div>
                <RegisterB
                  onSuccess={handleRegisterSuccess}
                  validators={validationProps}
                />
              </div>
            </motion.div>
          ) : activeTab === "word" || activeTab === "excel" || activeTab === "gukora-amaraporo" ? (
            <motion.div key="gukora-amaraporo" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} style={{ height: "100%" }}>
              <GukoraAmaraporo />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <style>{getMarkdownStyles()}</style>

      {/* Report Detail Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg" className="raporo-detail-modal">
        <Modal.Header closeButton className="raporo-header border-0"><Modal.Title className="text-white fw-bold fs-5 text-center">Ibisobanuro by'iyi Raporo</Modal.Title></Modal.Header>
        <Modal.Body className="p-3 p-md-4">
          {selectedReport && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <h3 className="raporo-title text-center mb-4" style={{ fontSize: "clamp(1.2rem, 4vw, 1.8rem)" }}>{selectedReport.umutwe_wa_raporo}</h3>
              <div className="p-4 bg-dark bg-opacity-50 rounded-3 mb-5" dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedReport.ibisobanuro || "") }} />
              <Row className="g-3">
                <Col xs={12} md={4}><div className="text-center p-3 border rounded-3"><div className="text-soft mb-2">Abaturage</div><div className="fs-3 fw-bold">{selectedReport.umubare_w_abaturage || 0}</div></div></Col>
                <Col xs={12} md={4}><div className="text-center p-3 border rounded-3"><div className="text-soft mb-2">Ingo</div><div className="fs-3 fw-bold">{selectedReport.umubare_w_ingo || 0}</div></div></Col>
                <Col xs={12} md={4}><div className="text-center p-3 border rounded-3"><div className="text-soft mb-2">Abanyantege nke</div><div className="fs-3 fw-bold text-danger">{selectedReport.abanyantege_nke || 0}</div></div></Col>
              </Row>
              {selectedReport.attachment && <div className="mt-5 text-center"><Button variant="primary" className="raporo-attachment-btn px-5 py-3" href={`${BASE_URL}${selectedReport.attachment}`} download><Icon.Download className="me-2" />Kuramo Dosiye</Button></div>}
            </motion.div>
          )}
        </Modal.Body>
      </Modal>

      {/* Logout Modal */}
      <Modal show={showLogout} onHide={() => setShowLogout(false)} centered size="md" className="logout-modal">
        <Modal.Body className="text-center py-5 px-5">
          <div className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle" style={{ width: 90, height: 90, backgroundColor: "var(--primary-soft)" }}><Icon.BoxArrowRight size={42} color="var(--primary)" /></div>
          <h4 className="fw-bold mb-3" style={{ color: "var(--text)" }}>Ushaka koko gusohoka?</h4>
          <p style={{ color: "var(--text-soft)", maxWidth: "380px", margin: "0 auto 1.5rem" }}>Uzasohoka muri konti yawe.</p>
          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-secondary" className="px-4 py-2 fw-medium" style={{ borderRadius: 10 }} onClick={() => setShowLogout(false)}>Oya, garuka</Button>
            <Button className="px-4 py-2 fw-medium" style={{ borderRadius: 10, background: "var(--primary)", border: "none", color: "#fff" }} onClick={() => { localStorage.removeItem("token"); navigate("/"); }}>Ego, sohoka</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="md" className="delete-modal">
        <Modal.Body className="text-center py-5 px-5">
          <div className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle" style={{ width: 90, height: 90, backgroundColor: "var(--danger-soft)" }}><Icon.Trash2Fill size={42} color="var(--danger)" /></div>
          <h4 className="fw-bold mb-3" style={{ color: "var(--text)" }}>Ushaka gusiba iyi raporo?</h4>
          <p className="fw-medium mb-1" style={{ color: "var(--text-soft)" }}>{reportToDelete ? reportToDelete.umutwe_wa_raporo : ""}</p>
          <p style={{ color: "var(--text-muted)", maxWidth: "380px", margin: "0 auto 1.5rem" }}>Izasibwa burundu.</p>
          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-secondary" className="px-4 py-2 fw-medium" style={{ borderRadius: 10 }} onClick={() => setShowDeleteConfirm(false)}>Oya, subika</Button>
            <Button variant="danger" className="px-4 py-2 fw-medium" style={{ borderRadius: 10 }} onClick={performDelete}>Ego, siba</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Mobile Bottom Navigation */}
      <nav className="akagari-bottom-nav d-lg-none">
        {[
          { label: "Statistics", icon: Icon.Grid,            tab: "raporo"        },
          { label: "Soma",      icon: Icon.FileEarmarkText,  tab: "gusoma-raporo" },
          { label: "Hindura Umuyobozi", icon: Icon.GeoAltFill, tab: "villages"    },
          { label: "Ubutumwa",  icon: Icon.ChatSquareText,   tab: "messages"      },
          { label: "Ongeraho Umuyobozi", icon: Icon.PersonPlus, tab: "ongeraho"   },
        ].map((item) => (
          <button
            key={item.tab}
            className={`akagari-bottom-nav-item${activeTab === item.tab ? " active" : ""}`}
            onClick={() => setActiveTab(item.tab)}
            aria-label={item.label}
          >
            <item.icon size={19} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default DashboardBohejuru;